import styled, { css } from 'styled-components';
import AdditionalDeliveryTooltip from './components/AdditionalDeliveryTooltip';
import Price from './components/Price';
import { PriceDiscount } from './components/PriceDiscount';
import PriceLabel from './components/PriceLabelRenderer';
import { RecommendedPrice } from './components/RecommendedPrice';
import type { PriceModuleProps } from './components/shared/types';

const PriceModuleContainer = styled.div`
    display: inline-grid;
    column-gap: ${({ theme }) => theme.Spacing4};
    text-align: left;

    display: inline-flex;
    flex-wrap: wrap;
    align-items: baseline;
`;

const StyledAdditionalDeliveryTooltip = styled(AdditionalDeliveryTooltip)`
    align-self: center;
`;

const PriceGrid = styled.div<{ $size?: PriceModuleProps['size'] }>`   
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;

    ${({ $size, theme }) =>
        $size === 'lg'
            ? css`
            gap: ${theme.Spacing8};
        `
            : css`
            gap: ${theme.Spacing4};
        `}
`;

const StyledPriceLabel = styled(PriceLabel)`
    flex: 0 0 100%;
`;

const StyledRecommendedPrice = styled(RecommendedPrice)<{ $fillLastLine: boolean }>`
    ${({ $fillLastLine }) =>
        $fillLastLine &&
        css`
            flex: 0 0 100%;
            order: 1;
        `}
`;

export default function PriceModule({
    price,
    priceLabel,
    priceBefore,
    saveLabel,
    pricePrefix,
    recommendedPriceLabel,
    showPossibleAdditionalDeliveryCost = false,
    size = 'sm',
    minimumSalesQuantity = 1,
    isOnDiscount = false,
    ...restProps
}: PriceModuleProps) {
    const showDiscount = isOnDiscount && !!priceBefore;
    const showRecommendedPrice = !!recommendedPriceLabel;

    return (
        <PriceModuleContainer {...restProps}>
            {priceLabel && <StyledPriceLabel priceLabel={priceLabel} size={size} />}
            {showDiscount || showRecommendedPrice || showPossibleAdditionalDeliveryCost ? (
                <PriceGrid $size={size}>
                    <Price price={price} pricePrefix={pricePrefix} size={size} isOnDiscount={isOnDiscount} />
                    {showDiscount && <PriceDiscount priceBefore={priceBefore} saveLabel={saveLabel} size={size} />}
                    {showRecommendedPrice && (
                        <StyledRecommendedPrice
                            recommendedPrice={recommendedPriceLabel}
                            size={size}
                            $fillLastLine={size !== 'lg'}
                        />
                    )}
                    {showPossibleAdditionalDeliveryCost && <StyledAdditionalDeliveryTooltip />}
                </PriceGrid>
            ) : (
                <Price price={price} pricePrefix={pricePrefix} size={size} />
            )}
        </PriceModuleContainer>
    );
}
